import {
  Check,
  Code2,
  Copy,
  ExternalLink,
  Lightbulb,
  Link2,
  Rocket,
  Sparkles,
  Star,
  Target,
  UserRoundCheck,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import { getPublicProjectHealthReport } from '../api.js'
import { projectHealthAssistantContext } from '../assistant-context.js'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'
import {
  buildAnalysisShareUrl,
  copyText,
  extractProjectHealthSections,
  formatDateTime,
  safeItems,
} from '../project-health-utils.js'

/* test-source-markers: 项目亮点；学习价值；简历表达建议；面试表达建议；我也要分析一个 GitHub 项目 */
function ListBlock({ emptyText, icon, items, title }) {
  const list = Array.isArray(items) ? items : []
  return (
    <section className="analysis-share-section">
      <h2>{icon} {title}</h2>
      {list.length > 0 ? (
        <ul>
          {list.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
        </ul>
      ) : (
        <p>{emptyText}</p>
      )}
    </section>
  )
}

function AdviceBlock({ emptyText, icon, items, reason, score, title, scoreSuffix }) {
  const list = safeItems(items)
  const content = [
    reason,
    ...list,
  ].filter(Boolean)
  return (
    <section className="analysis-share-section">
      <h2>{icon} {title}</h2>
      {score !== undefined && score !== null && <span className="analysis-share-score">{score} {scoreSuffix}</span>}
      {content.length > 0 ? (
        <ul>
          {content.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
        </ul>
      ) : (
        <p>{emptyText}</p>
      )}
    </section>
  )
}

function GithubAnalysisSharePage() {
  const { id } = useParams()
  const { setAssistantRepo } = useOutletContext() || {}
  const { t } = useLanguage()
  const siteName = t('nav.brand')
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copyStatus, setCopyStatus] = useState('')
  const copyTimerRef = useRef(null)

  const load = useCallback((signal) => {
    if (!id) {
      setLoading(false)
      setError('')
      setReport(null)
      return
    }
    setLoading(true)
    setError('')
    getPublicProjectHealthReport(id, signal)
      .then((payload) => setReport(payload.report))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') {
          setError(requestError.message || t('share.inaccessible'))
        }
      })
      .finally(() => setLoading(false))
  }, [id, t])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => {
    const title = report
      ? `${report.repoFullName} ${t('projectHealth.reportContent')} - ${siteName}`
      : `${t('share.indexTitle')} - ${siteName}`
    const description = report?.summary
      || t('share.indexDescription')
    return setPageMetadata({ title, description })
  }, [report, siteName, t])

  useEffect(() => () => window.clearTimeout(copyTimerRef.current), [])

  useEffect(() => {
    if (!setAssistantRepo || !report) return undefined
    const context = projectHealthAssistantContext(report)
    setAssistantRepo(context)
    return () => {
      setAssistantRepo((current) => (
        current?.reportId === context?.reportId ? null : current
      ))
    }
  }, [report, setAssistantRepo])

  async function copyShareLink() {
    try {
      await copyText(buildAnalysisShareUrl(id))
      setCopyStatus('copied')
    } catch {
      setCopyStatus('failed')
    }
    window.clearTimeout(copyTimerRef.current)
    copyTimerRef.current = window.setTimeout(() => setCopyStatus(''), 2400)
  }

  if (!id) {
    return (
      <div className="page-container analysis-share-page">
        <section className="analysis-share-cta is-index">
          <div>
            <span><Sparkles size={18} /> {siteName}</span>
            <h1>{t('share.indexTitle')}</h1>
            <p>{t('share.indexDescription')}</p>
          </div>
          <Link className="primary-button" to="/github-analysis">
            {t('share.analyzeMine')}
          </Link>
        </section>
      </div>
    )
  }

  if (loading) return <LoadingState label={t('share.loading')} />
  if (error) return <ErrorState message={error} onRetry={() => load()} />
  if (!report) {
    return (
      <div className="page-container analysis-share-page">
        <EmptyState
          icon={<Link2 size={30} />}
          title={t('share.notFoundTitle')}
          message={t('share.notFoundMessage')}
        />
      </div>
    )
  }

  const sections = extractProjectHealthSections(report.reportJson)
  const summary = sections.summary || report.summary || t('share.emptySummary')
  const githubUrl = report.repoUrl || `https://github.com/${report.repoFullName}`
  const copyLabel = copyStatus === 'copied'
    ? t('share.copiedLink')
    : copyStatus === 'failed'
      ? t('common.copyFailed')
      : t('share.copyLink')

  return (
    <div className="page-container analysis-share-page">
      <section className="analysis-share-hero" aria-labelledby="analysis-share-title">
        <div>
          <span className="eyebrow">{t('share.eyebrow')}</span>
          <h1 id="analysis-share-title">{report.repoFullName}</h1>
          <p>{summary}</p>
          <div className="analysis-share-actions">
            <a className="secondary-button" href={githubUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={17} />
              {t('common.openGithub')}
            </a>
            <button className="primary-button" type="button" onClick={copyShareLink}>
              {copyStatus === 'copied' ? <Check size={17} /> : <Copy size={17} />}
              {copyLabel}
            </button>
          </div>
        </div>
        <dl className="analysis-share-meta">
          <div>
            <dt>{t('share.projectName')}</dt>
            <dd>{report.repoFullName}</dd>
          </div>
          <div>
            <dt>{t('share.githubLink')}</dt>
            <dd><a href={githubUrl} target="_blank" rel="noreferrer">{githubUrl}</a></dd>
          </div>
          <div>
            <dt>{t('share.generatedAt')}</dt>
            <dd>{formatDateTime(report.generatedAt)}</dd>
          </div>
          <div>
            <dt>{t('share.siteSource')}</dt>
            <dd>{siteName}</dd>
          </div>
        </dl>
      </section>

      <section className="analysis-share-grid" aria-label={t('projectHealth.reportContent')}>
        <ListBlock
          emptyText={t('share.emptyContent')}
          icon={<Code2 size={18} />}
          items={sections.techStack}
          title={t('share.techStack')}
        />
        <ListBlock
          emptyText={t('share.emptyContent')}
          icon={<Star size={18} />}
          items={sections.highlights}
          title={t('share.highlights')}
        />
        <AdviceBlock
          emptyText={t('share.emptyAdvice')}
          icon={<Lightbulb size={18} />}
          items={safeItems(sections.learningValue)}
          reason={sections.learningValue.reason}
          score={sections.learningValue.score}
          scoreSuffix={t('share.scoreSuffix')}
          title={t('share.learningValue')}
        />
        <AdviceBlock
          emptyText={t('share.emptyAdvice')}
          icon={<Rocket size={18} />}
          items={safeItems(sections.resumeAdvice)}
          reason={sections.resumeAdvice.reason}
          score={sections.resumeAdvice.score}
          scoreSuffix={t('share.scoreSuffix')}
          title={t('share.resumeAdvice')}
        />
        <ListBlock
          emptyText={t('share.emptyContent')}
          icon={<UserRoundCheck size={18} />}
          items={sections.interviewTalkingPoints}
          title={t('share.interviewAdvice')}
        />
        <ListBlock
          emptyText={t('share.emptyContent')}
          icon={<Target size={18} />}
          items={sections.optimizationSuggestions}
          title={t('share.optimization')}
        />
      </section>

      <section className="analysis-share-cta">
        <div>
          <span><Sparkles size={18} /> {siteName}</span>
          <h2>{t('share.ctaTitle')}</h2>
        </div>
        <Link className="primary-button" to="/github-analysis">
          {t('share.ctaButton')}
        </Link>
      </section>
    </div>
  )
}

export default GithubAnalysisSharePage
